import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'
import { useProcessorVersionQuery } from '../../../hooks/admin/useSystemHealth'
import { cn } from '../../../shared/utils/cn'

/**
 * What the Sigfox processor is actually running — its deployed build and the
 * Node version its worker process reports for ITSELF.
 *
 * The runtime half earns its place on this page: on 2026-08-24 an Azure stack
 * flip to Node 24 left the host unable to launch a matching worker, so it fell
 * back to a bundled prehistoric runtime and every function died at startup with
 * "Use of const in strict mode" — while the portal still displayed Node 24.
 * Only the worker's own process.version separates a real upgrade from that
 * fallback, which is why `configured` (what Azure was asked for) is shown
 * beside `node` (what is running) and flagged when they disagree.
 */
export function ProcessorBuildStrip() {
  const { data, isLoading } = useProcessorVersionQuery()

  if (isLoading) {
    return <p className="text-xs text-gray-400">Checking processor build…</p>
  }

  if (!data?.reachable) {
    return (
      <p className="text-xs text-amber-700">
        <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1.5 h-3 w-3" />
        Processor version unavailable{data?.error ? ` — ${data.error}` : ''}
      </p>
    )
  }

  return (
    <dl className="flex flex-wrap items-baseline gap-x-6 gap-y-2 text-xs">
      <Field label="Processor build" value={data.gitHash} mono />
      <Field label="Version" value={data.version} />
      <Field label="Node" value={data.node} mono mismatch={isMismatched(data.configured, data.node)} />
      <Field label="Platform" value={platformLabel(data.platform, data.arch)} />
    </dl>
  )
}

/**
 * `configured` is the Azure setting (e.g. "~24"); `node` is the real runtime
 * (e.g. "v24.18.0"). A disagreement in the major version is the fallback
 * signature. Anything unparseable is treated as "no opinion", never a warning.
 */
function isMismatched(configured?: string | null, node?: string | null): boolean {
  if (!configured || !node) return false
  const want = configured.match(/(\d+)/)?.[1]
  const have = node.match(/(\d+)/)?.[1]
  if (!want || !have) return false
  return want !== have
}

function platformLabel(platform?: string | null, arch?: string | null): string | null {
  if (!platform && !arch) return null
  return [platform, arch].filter(Boolean).join(' ')
}

function Field({
  label,
  value,
  mono,
  mismatch,
}: {
  label: string
  value?: string | null
  mono?: boolean
  mismatch?: boolean
}) {
  return (
    <div className="flex items-baseline gap-1.5">
      <dt className="uppercase tracking-wide text-gray-500">{label}</dt>
      <dd
        className={cn(
          'font-medium',
          mono && 'font-mono',
          mismatch ? 'text-amber-700' : 'text-gray-900'
        )}
        title={mismatch ? 'Azure is configured for a different major version than the worker is running' : undefined}
      >
        {value ?? '—'}
        {mismatch && (
          <FontAwesomeIcon icon={faTriangleExclamation} className="ml-1.5 h-3 w-3" />
        )}
      </dd>
    </div>
  )
}
